import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Badge } from 'react-bootstrap'
import { amazonAPI } from '../api/amazonAPI'
import ResizableTable from '../components/ResizableTable'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function AmazonAccountsListScreen() {
    const { hasPermission } = usePermissions()
    const [accounts, setAccounts] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [testingId, setTestingId] = useState(null)
    const [testResults, setTestResults] = useState({})
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    useEffect(() => {
        fetchAccounts()
    }, [])

    async function fetchAccounts() {
        try {
            setLoading(true)
            const data = await amazonAPI.accounts.getAll()
            setAccounts(data)
        } finally {
            setLoading(false)
        }
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
    }

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            () => {},
            debounceTimeoutRef
        )
    }

    const handleTestConnection = async (e, accountId) => {
        e.stopPropagation()
        setTestingId(accountId)
        setTestResults(prev => ({ ...prev, [accountId]: null }))
        try {
            const result = await amazonAPI.accounts.testConnection(accountId)
            setTestResults(prev => ({ ...prev, [accountId]: { success: true, message: result.message || 'Connection successful' } }))
        } catch (error) {
            setTestResults(prev => ({ ...prev, [accountId]: { success: false, message: error.response?.data?.error || 'Connection failed' } }))
        } finally {
            setTestingId(null)
        }
    }

    const filteredAccounts = accounts.filter(account => {
        if (!searchTerm) return true
        const term = searchTerm.toLowerCase()
        return (
            account.account_name?.toLowerCase().includes(term)
        )
    })

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Amazon Accounts</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search accounts..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    {hasPermission('amazon.manage') && (
                        <Button
                            onClick={() => navigate('/amazon-account/create')}
                            variant="success"
                        >
                            Create Account
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <ResizableTable
                    tableId="amazon_accounts_table"
                    columns={[
                        {
                            id: 'account_name',
                            header: 'Account Name',
                            cell: (row) => highlightText(row.account_name, searchTerm)
                        },
                        {
                            id: 'status',
                            header: 'Status',
                            cell: (row) => (
                                <Badge bg={row.is_active ? 'success' : 'secondary'}>
                                    {row.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            )
                        },
                        {
                            id: 'connection',
                            header: 'Connection',
                            cell: (row) => (
                                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={(e) => handleTestConnection(e, row.id)}
                                        disabled={testingId === row.id || !row.is_active}
                                    >
                                        {testingId === row.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            'Test'
                                        )}
                                    </Button>
                                    {testResults[row.id] && (
                                        <small className={`ms-2 ${testResults[row.id].success ? 'text-success' : 'text-danger'}`}>
                                            {testResults[row.id].success ? '✓' : '✗'} {testResults[row.id].message}
                                        </small>
                                    )}
                                </div>
                            )
                        },
                    ]}
                    data={filteredAccounts}
                    initialColumnWidths={{
                        account_name: 'auto',
                        status: 100,
                        connection: 250,
                    }}
                    onRowClick={(row) => hasPermission('amazon.manage') ? navigate(`/amazon-account/${row.id}`) : null}
                    striped
                    hover
                />
            )}
        </Container>
    )
}

export default AmazonAccountsListScreen
